import React, { useEffect, useRef } from 'react'
import { Button, Card, Col, Form, Row } from 'react-bootstrap'

export default function RoleForm({
  state,
  errors,
  setState,
  children
}) {
  const refs = {
    form: useRef(null),
    users: useRef(null),
    roles: useRef(null),
    events: useRef(null),
    library: useRef(null),
    messages: useRef(null),
    locations: useRef(null),
    telehealth: useRef(null)
  }
  
  useEffect(() => {
    // Set inital values
    Object.values(state).forEach(permissions => {
      if (Array.isArray(permissions) && permissions.length > 0) {
        permissions.forEach(permission => {
          const element = document.getElementsByName(permission);

          if (element.length > 0) {
            element[0].checked = true
          }
        })
      }
    })

    // Update state with values
    Object.keys(refs).forEach(name => {
      const checked = []
      const options = refs[name].current.getElementsByTagName('input')

      for (let i = 0; i < options.length; ++i) {
        if (options[i].checked) {
          checked.push(options[i].value)
        } 
      }

      setState({[name]: checked})
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  

  const renderErrorMessage = (value) => {
    return errors[value] && <small className="text-danger">
      {errors[value]}
    </small>
  }

  const handleSetViewPermission = (permission) => {
    const value = `${permission}-view`

    if (state[permission].includes(value)) {
      setState({[permission]: state[permission].filter(item => item !== value)})
    } else { 
      setState({[permission]: [...state[permission], value]})
    }
  }

  const handleSetPermissions = (name, ref, select = null) => {
    let checked = []
    const options = ref.current.getElementsByTagName('input')
    
    for (let i = 0; i < options.length; ++i) {
      if (options[i].name.includes('view')) {
        options[i].checked = true
      }

      if (select === 'all') {
        options[i].checked = true
        checked.push(options[i].value)
      } else if (select === 'clear') {
        options[i].checked = false
      } else {
        if (options[i].checked) {
          checked.push(options[i].value)
        } 
      }
    }

    setState({[name]: checked})
  }

  const handleAllPermissions = (name, ref) => {
    const select = isAllSelected(ref) ? 'clear' : 'all'
    handleSetPermissions(name, ref, select)
  }

  const isAllSelected = (ref) => {
    if (ref.current) {
      const options = ref.current.getElementsByTagName('input')

      for (let i = 0; i < options.length; ++i) {
        if (!options[i].checked) {
          return false
        }
      }
      return true
    }
  }

  return (
    <Card id="addRoleForm" className='border-0 axs-shadow axs-rounded'>
      <Card.Body>

        {/* Name */}
        <Row>
          <Col xs='4' className='axs-font-weight-medium'>Organization Role</Col>
          <Col xs='4'>
            <Row>
              <Col>
                <Form.Group controlId="formRoleName">
                  <Form.Control 
                    type="text" 
                    onChange={(e) => setState({role: e.target.value})} 
                    placeholder="Ex: Front Office" 
                    className={errors.role && 'is-invalid'}
                    defaultValue={state.role}
                  />
                  {renderErrorMessage('role')}
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Description */}
        <hr />
        <Row>
          <Col xs='4' className='axs-font-weight-medium'>Description</Col>
          <Col xs='4'>
            <Row>
              <Col>
                <Form.Group controlId="formDescription">
                  <Form.Control 
                    type="text" 
                    onChange={(e) => setState({description: e.target.value})} 
                    placeholder="Enter your role description..." 
                    className={errors.description && 'is-invalid'}
                    defaultValue={state.description}
                  />
                  {renderErrorMessage('description')}
                </Form.Group>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Patient Forms */}
        <hr />
        <Row ref={refs.form}>
          <Col xs='4'>
            <Row className='mb-2'>
              <Col className='axs-font-weight-medium'>
                Patient Forms
              </Col>
            </Row>
            <Row>
              <Col className='text-muted'>
                <small>Patient Forms allow you to send registration, patient satisfaction, instructions, etc.</small>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label">
                    <input 
                      type="checkbox" 
                      name='form-view'
                      value='form-view'
                      disabled={state.form.length > 1}
                      className="form-check-input" 
                      onChange={() => handleSetViewPermission('form')} 
                    />
                    View Documents
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label">
                    <input 
                      type="checkbox" 
                      name='form-send'
                      value='form-send'
                      className="form-check-input" 
                      onChange={() => handleSetPermissions('form', refs.form)} 
                    />
                    Send Documents
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='mb-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label">
                    <input 
                      type="checkbox" 
                      name='form-reschedule'
                      value='form-reschedule'
                      className="form-check-input"
                      data-all-checked="false" 
                      onChange={() => handleSetPermissions('form', refs.form)} 
                    />
                    Reschedule Documents
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='text-left pl-0' >
                <Button 
                  onClick={() => handleAllPermissions('form', refs.form)} 
                  variant='link'>{ isAllSelected(refs.form) ? 'Clear All' : 'Select All' }
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label">
                    <input 
                      type="checkbox" 
                      name='form-cancel'
                      value='form-cancel'
                      className="form-check-input" 
                      onChange={() => handleSetPermissions('form', refs.form)} 
                    />
                    Cancel Documents
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label">
                    <input 
                      type="checkbox" 
                      name='form-export'
                      value='form-export'
                      className="form-check-input" 
                      onChange={() => handleSetPermissions('form', refs.form)} 
                    />
                    Export Documents Data
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Telehealth */}
        <hr />
        <Row ref={refs.telehealth}>
          <Col xs='4'>
            <Row className='mb-2'>
              <Col className='axs-font-weight-medium'>
                Telehealth
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='telehealth-view' 
                      value='telehealth-view' 
                      disabled={state.telehealth.length > 1}
                      onChange={() => handleSetViewPermission('telehealth')}
                      className="form-check-input" 
                    />
                    View Telehealth
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='telehealth-schedule' 
                      value='telehealth-schedule' 
                      onChange={() => handleSetPermissions('telehealth', refs.telehealth)}  
                      className="form-check-input" 
                    />
                    Schedule Telehealth
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='mb-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='telehealth-reschedule'
                      value='telehealth-reschedule'
                      onChange={() => handleSetPermissions('telehealth', refs.telehealth)}  
                      className="form-check-input" 
                    />
                    Reschedule Telehealth
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='text-left pl-0' >
                <Button 
                  onClick={() => handleAllPermissions('telehealth', refs.telehealth)} 
                  variant='link'>{ isAllSelected(refs.telehealth) ? 'Clear All' : 'Select All' }
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='telehealth-cancel'
                      value='telehealth-cancel'
                      onChange={() => handleSetPermissions('telehealth', refs.telehealth)}  
                      className="form-check-input" 
                    />
                    Cancel Telehealth
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='telehealth-room-access'
                      value='telehealth-room-access'
                      onChange={() => handleSetPermissions('telehealth', refs.telehealth)}  
                      className="form-check-input" 
                    />
                    Telehealth Room Access &nbsp;
                  </label>
                  {/* <i className="fas fa-info-circle cursor-pointer"
                    onClick={() => setState({showTelehealthAccessCard: !state.showTelehealthAccessCard})} >  
                  </i> */}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Events */}
        <hr />
        <Row ref={refs.events}>
          <Col xs='4'>
            <Row className='mb-2'>
              <Col className='axs-font-weight-medium'>
                Events
              </Col>
            </Row>
            <Row>
              <Col className='text-muted'>
                <small>&nbsp;</small>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='events-view'
                      value='events-view'
                      disabled={state.events.length > 1}
                      onChange={() => handleSetViewPermission('events')}
                      className="form-check-input" 
                    />
                    View Events
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='events-schedule' 
                      value='events-schedule' 
                      onChange={() => handleSetPermissions('events', refs.events)}  
                      className="form-check-input" 
                    />
                    Schedule Events
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='mb-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='events-reschedule'
                      value='events-reschedule'
                      onChange={() => handleSetPermissions('events', refs.events)}  
                      className="form-check-input" 
                    />
                    Reschedule Events
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='text-left pl-0' >
                <Button 
                  onClick={() => handleAllPermissions('events', refs.events)} 
                  variant='link'>{ isAllSelected(refs.events) ? 'Clear All' : 'Select All' }
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='events-cancel' 
                      value='events-cancel' 
                      onChange={() => handleSetPermissions('events', refs.events)}  
                      className="form-check-input" 
                    />
                    Cancel Events
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='events-edit' 
                      value='events-edit' 
                      onChange={() => handleSetPermissions('events', refs.events)}  
                      className="form-check-input" 
                    />
                    Edit Events
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Patient Messages */}
        <hr />
        <Row ref={refs.messages}>
          <Col xs='4'>
            <Row className='mb-2'>
              <Col className='axs-font-weight-medium'>
                Patient Messages
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='messages-view' 
                      value='messages-view' 
                      disabled={state.messages.length > 1}
                      onChange={() => handleSetViewPermission('messages')}
                      className="form-check-input" 
                    />
                    View Messages
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='messages-send' 
                      value='messages-send' 
                      onChange={() => handleSetPermissions('messages', refs.messages)}  
                      className="form-check-input" 
                    />
                    Send Message
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='text-left pl-0' >
                <Button 
                  onClick={() => handleAllPermissions('messages', refs.messages)} 
                  variant='link'>{ isAllSelected(refs.messages) ? 'Clear All' : 'Select All' }
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='messages-close' 
                      value='messages-close' 
                      onChange={() => handleSetPermissions('messages', refs.messages)}  
                      className="form-check-input" 
                    />
                    Close Message
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='messages-reopen' 
                      value='messages-reopen' 
                      onChange={() => handleSetPermissions('messages', refs.messages)}  
                      className="form-check-input" 
                    />
                    Reopen Message
                  </label>
                </div>
              </Col>
            </Row>
            <Row className='my-2'>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='messages-set-read' 
                      value='messages-set-read' 
                      onChange={() => handleSetPermissions('messages', refs.messages)}  
                      className="form-check-input" 
                    />
                    Set as Read
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Manage Library */}
        <hr />
        <Row ref={refs.library}>
          <Col xs='4'>
            <Row className='mb-2'>
              <Col className='axs-font-weight-medium'>Manage Library</Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='library-questions'
                      value='library-questions'
                      onChange={() => handleSetPermissions('library', refs.library)}  
                      className="form-check-input" 
                    />
                    Manage Question
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='text-left pl-0' >
                <Button 
                  onClick={() => handleAllPermissions('library', refs.library)} 
                  variant='link'>{ isAllSelected(refs.library) ? 'Clear All' : 'Select All' }
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='library-forms' 
                      value='library-forms' 
                      onChange={() => handleSetPermissions('library', refs.library)}  
                      className="form-check-input" 
                    />
                    Manage Forms
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Manage User */}
        <hr />
        <Row>
          <Col xs='4'>
            <Row className='mb-2'>
              <Col className='axs-font-weight-medium'>
                Manage Users
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check" ref={refs.users}>
                  <label className="form-check-label">
                    <input 
                      type="checkbox" 
                      name='users-manage' 
                      value='users-manage' 
                      onChange={() => handleSetPermissions('users', refs.users)}  
                      className="form-check-input" 
                    />
                    Manage User
                  </label>
                </div>
              </Col>
            </Row>
            <Row>
              <Col className='text-left pl-0' >
                <Button 
                  onClick={() => handleAllPermissions('users', refs.users)} 
                  variant='link'>{ isAllSelected(refs.users) ? 'Clear All' : 'Select All' }
                </Button>
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check" ref={refs.roles}>
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='roles-manage' 
                      value='roles-manage' 
                      onChange={() => handleSetPermissions('roles', refs.roles)}  
                      className="form-check-input" 
                    />
                    Manage Role
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        {/* Manage Locations */}
        <hr />
        <Row ref={refs.locations}>
          <Col xs='4'>
            <Row className='mb-2'>
              <Col className='axs-font-weight-medium'>
                Manage Locations
              </Col>
            </Row>
          </Col>
          <Col xs='4' className='font-weight-light text-muted'>
            <Row>
              <Col>
                <div className="form-check">
                  <label className="form-check-label" >
                    <input 
                      type="checkbox" 
                      name='locations-manage' 
                      value='locations-manage' 
                      onChange={() => handleSetPermissions('locations', refs.locations)}  
                      className="form-check-input" 
                    />
                    Manage Locations
                  </label>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>

        { children }

      </Card.Body>
    </Card>
  )
}
